import { useEffect, useMemo, useState } from "react";
import { app } from "@microsoft/teams-js";

export type TeamsUserData = {
  userId?: string;
  displayName?: string;
  loginHint?: string;
  userPrincipalName?: string;
  tenantId?: string;
};

type TeamsUserState = {
  isInTeams: boolean;
  status: "loading" | "ready" | "unavailable" | "error";
  user: TeamsUserData | null;
};

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(new Error("Timed out while loading Teams user context."));
    }, timeoutMs);

    promise.then(
      (value) => {
        window.clearTimeout(timeoutId);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      }
    );
  });
}

export function useTeamsUser(): TeamsUserState {
  const [state, setState] = useState<TeamsUserState>({
    isInTeams: false,
    status: "loading",
    user: null,
  });

  useEffect(() => {
    let disposed = false;

    async function loadTeamsUser() {
      try {
        await withTimeout(app.initialize(), 1500);
        const context = await withTimeout(app.getContext(), 1500);

        if (disposed) return;

        setState({
          isInTeams: true,
          status: "ready",
          user: {
            userId: context.user?.id,
            displayName: context.user?.displayName,
            loginHint: context.user?.loginHint,
            userPrincipalName: context.user?.userPrincipalName,
            tenantId: context.user?.tenant?.id,
          },
        });
      } catch {
        if (disposed) return;

        setState({
          isInTeams: false,
          status: "unavailable",
          user: null,
        });
      }
    }

    void loadTeamsUser();

    return () => {
      disposed = true;
    };
  }, []);

  return useMemo(() => state, [state]);
}